import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { featuredAPI } from '../lib/api';
import {
  Building2,
  Map,
  Users,
  Package,
  Calculator,
  Shield,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Star,
  Loader2,
}from 'lucide-react';

const LandingPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [featuredBuilders, setFeaturedBuilders] = useState([]);
  const [loadingBuilders, setLoadingBuilders] = useState(true);

  useEffect(() => {
    loadFeaturedBuilders();
  }, []);

  const loadFeaturedBuilders = async () => {
    try {
      const response = await featuredAPI.getBuilders();
      setFeaturedBuilders(response.data.builders || []);
    } catch (error) {
      console.error('Failed to load featured builders:', error);
    } finally {
      setLoadingBuilders(false);
    }
  };

  const features = [
    {
      icon: Map,
      title: 'Land Registration',
      description: 'Register and manage your land parcels with geo-tagged locations and real-time valuations.',
    },
    {
      icon: Users,
      title: 'Verified Builders',
      description: 'Access a network of verified builders with performance history and skill profiles.',
    },
    {
      icon: Package,
      title: 'Supplier Network',
      description: 'Connect with suppliers for competitive material pricing and reliable delivery.',
    },
    {
      icon: Calculator,
      title: 'Land Valuation',
      description: 'Get instant land value estimates based on market data and comparable sales.',
    },
    {
      icon: Shield,
      title: 'Transparent Tracking',
      description: 'Track project stages, budgets, and milestones with complete transparency.',
    },
    {
      icon: TrendingUp,
      title: 'Financial Insights',
      description: 'Access construction-linked finance and embedded payment solutions.',
    },
  ];

  const stats = [
    { value: '500+', label: 'Active Projects' },
    { value: '200+', label: 'Verified Builders' },
    { value: '50+', label: 'Material Suppliers' },
    { value: 'BWP 100M+', label: 'Project Value' },
  ];

  const testimonials = [
    {
      name: 'Kgosi Mosimanegape',
      role: 'Landowner, Gaborone',
      content: 'Finally, a platform that brings transparency to construction. I can track every stage of my project.',
      rating: 5,
    },
    {
      name: 'Lesedi Builders',
      role: 'Construction Company',
      content: 'The builder verification system has helped us gain more trust from clients. Great platform!',
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="logo">
              <div className="w-10 h-10 bg-[#0F2640] rounded-sm flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-foreground">
                Land Builders
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors">
                Features
              </a>
              <a href="#how-it-works" className="text-muted-foreground hover:text-foreground transition-colors">
                How It Works
              </a>
              <a href="#testimonials" className="text-muted-foreground hover:text-foreground transition-colors">
                Testimonials
              </a>
            </nav>

            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <Button onClick={() => navigate('/dashboard')} data-testid="dashboard-btn">
                  Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <>
                  <Button variant="ghost" asChild data-testid="login-btn">
                    <Link to="/login">Login</Link>
                  </Button>
                  <Button asChild data-testid="get-started-btn">
                    <Link to="/register">Get Started</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1645434897689-af222b85993e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwbW9kZXJuJTIwYnVpbGRpbmclMjBzdHJ1Y3R1cmV8ZW58MHx8fHwxNzY4MjQ4NTgxfDA&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="hero-overlay absolute inset-0" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 text-white">
              <p className="inline-flex items-center px-3 py-1 bg-white/10 backdrop-blur rounded-full text-sm mb-6 animate-fade-in">
                <span className="w-2 h-2 bg-[#10B981] rounded-full mr-2" />
                Botswana's Trusted Construction Platform
              </p>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 animate-fade-in stagger-1">
                Build Reliable.
                <br />
                <span className="text-[#10B981]">Build Transparent.</span>
                <br />
                Build Bankable.
              </h1>
              
              <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-xl animate-fade-in stagger-2">
                A unified digital ecosystem connecting landowners, builders, and suppliers 
                for trustworthy construction projects across Botswana.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in stagger-3">
                <Button 
                  size="lg" 
                  className="bg-[#10B981] hover:bg-[#0ea572] text-white"
                  onClick={() => navigate('/register')}
                  data-testid="hero-get-started"
                >
                  Start Your Project
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  onClick={() => navigate('/builders')}
                  data-testid="hero-find-builders"
                >
                  Find Builders
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 pt-12 border-t border-white/20 animate-fade-in stagger-4">
                {stats.map((stat, index) => (
                  <div key={index}>
                    <p className="font-heading text-2xl sm:text-3xl font-bold text-white">
                      {stat.value}
                    </p>
                    <p className="text-sm text-white/60">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden lg:block lg:col-span-5">
              {/* Floating card */}
              <Card className="bg-white/95 backdrop-blur shadow-2xl animate-slide-up">
                <CardContent className="p-6">
                  <h3 className="font-heading font-bold text-lg mb-4">Quick Valuation</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Location</p>
                      <p className="font-medium">Gaborone, Block 9</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Land Size</p>
                      <p className="font-medium">1,200 sqm</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Estimated Value</p>
                      <p className="font-heading text-2xl font-bold text-[#10B981]">
                        BWP 1,980,000
                      </p>
                    </div>
                    <Button className="w-full" onClick={() => navigate('/valuation')}>
                      Get Your Valuation
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Everything You Need to Build
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From land registration to project completion, we provide all the tools 
              and connections you need for successful construction.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="border border-border hover:border-[#10B981]/50 transition-all card-hover"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-[#0F2640] rounded-sm flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
              How It Works
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Get your construction project started in four simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Register Your Land', desc: 'Add your land details and get instant valuation' },
              { step: '02', title: 'Create Project', desc: 'Define scope, budget, and timeline' },
              { step: '03', title: 'Find Builders', desc: 'Match with verified builders for your project' },
              { step: '04', title: 'Track Progress', desc: 'Monitor stages, costs, and milestones' },
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-heading font-bold text-muted/30 mb-4">
                  {item.step}
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
                {index < 3 && (
                  <ArrowRight className="hidden lg:block absolute top-8 -right-4 w-8 h-8 text-muted-foreground/30" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-20 bg-[#0F2640]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1628900941064-ba8df8b51e4a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50cyUyMHBsYW5uaW5nJTIwdGFibGV8ZW58MHx8fHwxNzY4MjQ4NTg0fDA&ixlib=rb-4.1.0&q=85"
                alt="Architectural blueprints and planning"
                className="rounded-sm shadow-2xl"
              />
            </div>
            <div className="text-white">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">
                Professional Planning & Execution
              </h2>
              <p className="text-white/80 text-lg mb-8">
                Our platform ensures every construction project follows standardized stages, 
                from site preparation to final inspection. Track progress, manage budgets, 
                and ensure quality at every step.
              </p>
              <ul className="space-y-4">
                {[
                  'Standardized construction stages',
                  'Real-time progress tracking',
                  'Budget management tools',
                  'Quality assurance checkpoints',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Trusted by Builders & Landowners
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border border-border">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-amber-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-lg mb-4">"{testimonial.content}"</p>
                  <div>
                    <p className="font-medium">{testimonial.name}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Builders Section */}
      <section id="featured-builders" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Featured Builders
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Connect with our top-rated, verified builders ready to bring your construction dreams to life.
            </p>
          </div>

          {loadingBuilders ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : featuredBuilders.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredBuilders.slice(0, 6).map((builder) => (
                <Card 
                  key={builder.builder_id} 
                  className="border border-border hover:border-[#10B981]/50 transition-all card-hover"
                  data-testid={`featured-builder-${builder.builder_id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 bg-[#0F2640] rounded-full flex items-center justify-center text-white font-heading font-bold text-lg">
                          {builder.user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div>
                          <h3 className="font-heading font-bold">{builder.user?.name}</h3>
                          <div className="flex items-center gap-1 text-sm">
                            <Star className="w-4 h-4 text-amber-400 fill-current" />
                            <span className="font-medium">{builder.rating?.toFixed(1)}</span>
                            <span className="text-muted-foreground">
                              ({builder.total_projects} projects)
                            </span>
                          </div>
                        </div>
                      </div>
                      {builder.verified && (
                        <span className="bg-emerald-100 text-emerald-700 px-2 py-1 text-xs font-medium rounded flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          Verified
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {builder.skills?.slice(0, 3).map((skill) => (
                        <span
                          key={skill}
                          className="bg-muted text-muted-foreground px-2 py-1 text-xs rounded"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>{builder.experience_years} years exp.</span>
                      {builder.hourly_rate > 0 && (
                        <span className="font-mono font-medium text-foreground">
                          BWP {builder.hourly_rate}/hr
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Featured builders coming soon!</p>
            </div>
          )}

          <div className="text-center mt-8">
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => navigate('/builders')}
              data-testid="view-all-builders-btn"
            >
              View All Builders
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0F2640]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Start Building?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Join hundreds of landowners and builders already using Land Builders 
            to create transparent, reliable construction projects.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-[#10B981] hover:bg-[#0ea572] text-white"
              onClick={() => navigate('/register')}
              data-testid="cta-register"
            >
              Create Free Account
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              onClick={() => navigate('/login')}
              data-testid="cta-login"
            >
              Sign In
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <Link to="/" className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#0F2640] rounded-sm flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <span className="font-heading font-bold text-xl">Land Builders</span>
              </Link>
              <p className="text-muted-foreground">
                Building Botswana's future, one project at a time.
              </p>
            </div>
            
            <div>
              <h4 className="font-heading font-bold mb-4">Platform</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/builders" className="hover:text-foreground">Find Builders</Link></li>
                <li><Link to="/suppliers" className="hover:text-foreground">Suppliers</Link></li>
                <li><Link to="/valuation" className="hover:text-foreground">Land Valuation</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-heading font-bold mb-4">Company</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><a href="#" className="hover:text-foreground">About Us</a></li>
                <li><a href="#" className="hover:text-foreground">Contact</a></li>
                <li><a href="#" className="hover:text-foreground">Careers</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-heading font-bold mb-4">Legal</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><a href="#" className="hover:text-foreground">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-foreground">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-border text-center text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} Land Builders Botswana. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
